import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { perDay } from '@/data/plans'

export const FAQS = [
  { q: 'What are slices?', a: 'Slices are included with your plan each month. You give them to works and creators you love. Unused slices roll into the Community Fund that subscribers vote on monthly.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel whenever you like and keep access until the end of your billing period.' },
  { q: 'How do I change or manage my plan?', a: 'Once subscribed, manage your plan and billing details from your account at any time.' },
  { q: 'Is annual billing available?', a: 'Annual billing with two months free is launching soon. Monthly plans are available today.' },
]

/** Plan features without the "N slices/month" line — slices get their own treatment. */
export function planFeatures(plan) {
  return plan.features.filter((f) => !/slices\/month/.test(f))
}

export function priceFor(plan, isAnnual) {
  return isAnnual ? plan.annualPrice : plan.monthlyPrice
}

export function priceNote(plan, isAnnual) {
  return isAnnual
    ? `$${(plan.annualPrice / 12).toFixed(2)}/mo billed annually`
    : `${perDay(plan.monthlyPrice)}/day`
}

export function BillingToggle({ billing, setBilling, className = '' }) {
  return (
    <div className={`inline-flex items-center rounded-full border border-border bg-card/60 p-1 ${className}`}>
      {['monthly', 'annual'].map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => setBilling(mode)}
          aria-pressed={billing === mode}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            billing === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {mode === 'monthly' ? 'Monthly' : 'Annual'}
          {mode === 'annual' && (
            <span className={`ml-1.5 text-xs font-semibold ${billing === 'annual' ? 'text-primary-foreground/80' : 'text-primary'}`}>
              −2 months
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/** The plan's call to action — covers guest, subscribing, current-plan and switch states. */
export function PlanCTA({ plan, ctx, variant, className = '', label }) {
  const { isSubscribed, profile, subscribing, managingPortal, handleSubscribe, handleManageSubscription } = ctx
  const isCurrent = isSubscribed && profile?.subscription_tier === plan.tier

  if (isCurrent) {
    return (
      <Button variant="outline" className={className} disabled>
        Current plan
      </Button>
    )
  }
  if (isSubscribed) {
    return (
      <Button variant="outline" className={className} onClick={handleManageSubscription} disabled={managingPortal}>
        {managingPortal && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Switch to {plan.tier}
      </Button>
    )
  }
  return (
    <Button
      className={className}
      variant={variant ?? (plan.featured ? 'default' : 'outline')}
      onClick={() => handleSubscribe(plan)}
      disabled={!!subscribing}
    >
      {subscribing === plan.tier && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {subscribing === plan.tier ? 'Redirecting…' : (label ?? `Start with ${plan.tier}`)}
    </Button>
  )
}

export function FaqList({ className = '' }) {
  return (
    <div className={`divide-y divide-border border-y border-border ${className}`}>
      {FAQS.map(({ q, a }) => (
        <details key={q} className="group py-4">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-foreground">
            {q}
            <span className="text-lg leading-none text-muted-foreground transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">{a}</p>
        </details>
      ))}
    </div>
  )
}

/** A single slice wedge — the visual unit for slices across layouts. */
export function SliceWedge({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M12 21 3.2 6.2A15 15 0 0 1 20.8 6.2Z" fill="currentColor" />
      <path d="M3.2 6.2A15 15 0 0 1 20.8 6.2" fill="none" stroke="currentColor" strokeOpacity=".55" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}
